import type {ILocationToken} from "./location-selection-types";

/** Semantic group metadata from the location field preset. */
export interface ISemanticGroup {
    code: string;
    label: string;
    memberCodes: string[];
}

export interface ISemanticGroupPartitionConfig {
    /** Returns the token.group value used for this group (code or label). */
    resolveGroupKey: (group: ISemanticGroup) => string;
    /** Whether a token counts as a country-only member eligible for full-set collapse. */
    isCountryOnlyMember: (
        token: ILocationToken,
        group: ISemanticGroup,
        groupKey: string
    ) => boolean;
}

export interface ISemanticGroupPartitionResult {
    /** Group keys (per resolveGroupKey) representing fully selected semantic groups. */
    fullGroupKeys: string[];
    remainingTokens: ILocationToken[];
}

/** True when a token represents a full country selection within a semantic group (no province/city). */
export function isSemanticGroupCountryOnlyMember(
    token: ILocationToken,
    group: ISemanticGroup,
    groupKey: string
): boolean {
    const memberCodeSet = new Set(group.memberCodes);

    return token.group === groupKey
        && Boolean(token.country)
        && memberCodeSet.has(token.country!)
        && !token.province
        && !token.city;
}

/**
 * Separates fully selected semantic groups from remaining location tokens.
 * Handles explicit group-only tokens and collapsing a complete country-only member set.
 */
export function partitionFullSemanticGroupSelections(
    tokens: ILocationToken[],
    semanticGroups: ISemanticGroup[],
    config: ISemanticGroupPartitionConfig
): ISemanticGroupPartitionResult {
    const remainingTokens = [...tokens];
    const fullGroupKeys: string[] = [];

    for (const group of semanticGroups) {
        const groupKey = config.resolveGroupKey(group);
        const explicitIndex = remainingTokens.findIndex(
            (token) => token.group === groupKey && !token.country
        );

        if (explicitIndex >= 0) {
            fullGroupKeys.push(groupKey);
            remainingTokens.splice(explicitIndex, 1);
        }
    }

    for (const group of semanticGroups) {
        const groupKey = config.resolveGroupKey(group);

        /** Completeness is decided by unique membership: duplicate tokens must not stand in for missing members. */
        const selectedMemberCodes = new Set(
            remainingTokens
                .filter((token) => config.isCountryOnlyMember(token, group, groupKey))
                .map((token) => token.country!)
        );

        if (selectedMemberCodes.size !== new Set(group.memberCodes).size) {
            continue;
        }

        for (let index = remainingTokens.length - 1; index >= 0; index -= 1) {
            const token = remainingTokens[index];
            if (config.isCountryOnlyMember(token, group, groupKey)) {
                remainingTokens.splice(index, 1);
            }
        }

        fullGroupKeys.push(groupKey);
    }

    return {fullGroupKeys, remainingTokens};
}
